import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, Download, CheckCircle2, Loader2, Send, BrainCircuit, Blocks, CalendarCheck, FileText, Wallet, FolderKanban } from "lucide-react";
import SiteNav from "@/components/SiteNav";
import SiteFooter from "@/components/SiteFooter";
import Seo from "@/components/Seo";
import api, { formatApiErrorDetail } from "@/lib/api";

const PACKET_URL = `${process.env.REACT_APP_BACKEND_URL}/api/ndis/info-packet`;

const inputCls = "w-full bg-[#050505] border border-white/10 px-4 py-3 font-mono text-sm text-zinc-100 rounded-none focus:outline-none focus:ring-2 focus:ring-nc-blue focus:border-nc-blue";
const labelCls = "font-mono text-[10px] tracking-[0.25em] text-zinc-400 uppercase block mb-2";

const OLD_WAY = [
  "Hundreds of hidden menus and settings to memorise",
  "Months of technical training before your first finished model",
  "Repetitive, detail-heavy steps that drain focus and energy",
  "Your creative ideas stay stuck — the tools get in the way",
];
const NEW_WAY = [
  "Describe or sketch your idea — our Image-to-3D engine builds the model",
  "Skip the technical bottlenecks and go straight to creating",
  "Hyperfocus becomes a strength: deep, visual, detail-driven work",
  "Every session ends with something finished you can see and keep",
];

const WEEKS = [
  {
    n: 1, title: "AI Engine Fundamentals & Spatial Design",
    points: ["Set up your personal creator workstation and tools", "Learn how Specpixel's Image-to-3D engine turns your ideas into models", "Practise core spatial design: shape, scale, and structure"],
    outcome: "Your first completed 3D model, made by you",
  },
  {
    n: 2, title: "Advanced Asset Structuring & Quality Control",
    points: ["Refine raw AI outputs into clean, professional models", "Learn the studio quality checklist used in real game development", "Organise your work into a structured asset pack"],
    outcome: "A studio-ready asset pack that passes professional review",
  },
  {
    n: 3, title: "Marketplace Integration & Storefront Architecture",
    points: ["Set up your digital creator accounts, step by step, with support", "Learn how online marketplaces for game assets work", "Build your own storefront page: titles, images, and descriptions"],
    outcome: "Your own live creator storefront, ready to stock",
  },
  {
    n: 4, title: "Freelance Systems & Independent Monetisation",
    points: ["Publish your asset pack online for the first time", "Learn the basics of pricing, licensing, and customer communication", "Build a simple weekly routine for ongoing independent work"],
    outcome: "A published, operational online portfolio — the foundation of your own digital micro-enterprise",
  },
];

const OUTCOMES = [
  { icon: FolderKanban, title: "A Published Portfolio", body: "Your asset packs are live online, visible to game developers around the world, 24 hours a day." },
  { icon: Blocks, title: "A Repeatable Skill", body: "You know the full pipeline from idea to published product, and can repeat it independently." },
  { icon: Wallet, title: "A Pathway to Income", body: "Your storefront keeps working between cohorts. Sales depend on the market, and no income is guaranteed — but the foundation is real, and it's yours." },
];

function IntakeForm() {
  const [form, setForm] = useState({
    participant_name: "", participant_age: "", contact_type: "participant", contact_name: "",
    email: "", phone: "", plan_type: "plan_managed", goal: "", website: "",
  });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/ndis/intake", { ...form, participant_age: parseInt(form.participant_age, 10) || 0 });
      setDone(true);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div data-testid="ndis-intake-success" className="border border-nc-green/40 bg-nc-green/5 p-8 text-center">
        <CheckCircle2 className="w-8 h-8 text-nc-green mx-auto mb-4" />
        <p className="font-sans font-semibold text-lg text-zinc-100">Thank you — your enquiry has been received.</p>
        <p className="font-mono text-xs text-zinc-400 mt-3 leading-relaxed max-w-md mx-auto">
          We'll reply within two business days with next steps, and we're happy to speak with your support coordinator or plan manager directly.
        </p>
      </div>
    );
  }

  return (
    <form data-testid="ndis-intake-form" onSubmit={submit} className="flex flex-col gap-5">
      <div className="grid grid-cols-1 sm:grid-cols-[2fr_1fr] gap-5">
        <div>
          <label className={labelCls} htmlFor="ndis-name">Participant Name</label>
          <input id="ndis-name" data-testid="ndis-name-input" required minLength={2} maxLength={80} className={inputCls} value={form.participant_name} onChange={(e) => set("participant_name", e.target.value)} />
        </div>
        <div>
          <label className={labelCls} htmlFor="ndis-age">Participant Age (16+)</label>
          <input id="ndis-age" data-testid="ndis-age-input" required type="number" min={16} max={99} className={inputCls} value={form.participant_age} onChange={(e) => set("participant_age", e.target.value)} />
        </div>
      </div>

      <div>
        <span className={labelCls}>Primary Contact</span>
        <div className="flex flex-col sm:flex-row gap-2" role="radiogroup" aria-label="Primary contact">
          {[["participant", "I am the participant"], ["parent_carer", "I am a parent, carer, or guardian"], ["coordinator", "I am a Support Coordinator"]].map(([v, label]) => (
            <button
              key={v} type="button" data-testid={`ndis-contact-${v}`}
              onClick={() => set("contact_type", v)}
              aria-pressed={form.contact_type === v}
              className={`flex-1 font-mono text-[11px] px-4 py-3 border transition-colors duration-300 text-left ${form.contact_type === v ? "border-nc-blue bg-nc-blue/10 text-zinc-100" : "border-white/10 text-zinc-400 hover:border-white/30"}`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {form.contact_type !== "participant" && (
        <div>
          <label className={labelCls} htmlFor="ndis-contact-name">Contact Name</label>
          <input id="ndis-contact-name" data-testid="ndis-contact-name-input" required maxLength={80} className={inputCls} value={form.contact_name} onChange={(e) => set("contact_name", e.target.value)} />
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <div>
          <label className={labelCls} htmlFor="ndis-email">Contact Email</label>
          <input id="ndis-email" data-testid="ndis-email-input" required type="email" className={inputCls} value={form.email} onChange={(e) => set("email", e.target.value)} />
        </div>
        <div>
          <label className={labelCls} htmlFor="ndis-phone">Contact Phone <span className="text-zinc-600 normal-case tracking-normal">(optional)</span></label>
          <input id="ndis-phone" data-testid="ndis-phone-input" type="tel" maxLength={30} className={inputCls} value={form.phone} onChange={(e) => set("phone", e.target.value)} />
        </div>
      </div>

      <div>
        <label className={labelCls} htmlFor="ndis-plan-type">NDIS Plan Management Type</label>
        <select id="ndis-plan-type" data-testid="ndis-plan-type-select" className={inputCls} value={form.plan_type} onChange={(e) => set("plan_type", e.target.value)}>
          <option value="plan_managed">Plan-Managed</option>
          <option value="self_managed">Self-Managed</option>
          <option value="agency_managed">Agency-Managed</option>
          <option value="not_sure">Not sure yet</option>
        </select>
      </div>

      <div>
        <label className={labelCls} htmlFor="ndis-goal">Primary Employment or Skill Goal</label>
        <textarea id="ndis-goal" data-testid="ndis-goal-input" required minLength={10} maxLength={2000} rows={4} className={`${inputCls} resize-y leading-relaxed`} placeholder="For example: I want to learn digital skills and work toward earning my own income online." value={form.goal} onChange={(e) => set("goal", e.target.value)} />
      </div>

      {/* honeypot — invisible to humans */}
      <div className="relative h-0 overflow-hidden" aria-hidden="true">
        <input type="text" name="website" data-testid="ndis-honeypot" value={form.website} onChange={(e) => set("website", e.target.value)} tabIndex={-1} autoComplete="off" />
      </div>

      <button data-testid="ndis-intake-submit" disabled={busy} type="submit" className="bg-nc-orange hover:bg-nc-orange/80 disabled:opacity-40 text-black font-sans font-semibold uppercase tracking-[0.2em] text-xs px-6 py-4 flex items-center justify-center gap-2 transition-colors w-full">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Submit NDIS Intake Enquiry
      </button>
      <p className="font-mono text-[10px] text-zinc-600 leading-relaxed">Your information is used only to respond to your enquiry. We never share participant details.</p>
    </form>
  );
}

export default function NdisJoinPage() {
  return (
    <div data-testid="ndis-join-page" className="min-h-screen bg-[#050505] text-zinc-100 flex flex-col selection:bg-nc-blue/30">
      <Seo
        title="NDIS AI Game-Dev Pilot Program — Build Creative Independence"
        description="A structured 4-week AI game development and 3D asset creation program for neurodivergent creators aged 16+. Open to Self-Managed and Plan-Managed NDIS participants. Capacity Building line items, measurable weekly outcomes, and a published portfolio at completion."
        path="/join-creators-ndis"
        keywords="NDIS game development program, neurodivergent coding program Australia, ADHD autism digital skills training, NDIS capacity building employment, AI 3D asset creation course, NDIS micro-enterprise"
      />
      <SiteNav />
      <main className="flex-1">

        {/* 1 — HERO */}
        <section className="border-b border-white/10">
          <div className="max-w-4xl mx-auto px-6 py-20 sm:py-28">
            <p data-testid="ndis-hero-eyebrow" className="font-mono text-[10px] tracking-[0.35em] text-nc-orange uppercase mb-6">NDIS Participant Program — Ages 16+</p>
            <h1 data-testid="ndis-hero-title" className="font-sans font-bold text-4xl sm:text-5xl leading-tight tracking-tight text-zinc-100">
              Build Real Game-Development Skills. Create 3D Art With AI. Work Toward Your Own Income.
            </h1>
            <p data-testid="ndis-hero-sub" className="text-base sm:text-lg text-zinc-400 leading-relaxed mt-6 max-w-2xl">
              The Specpixel Advanced AI Game-Dev Pilot Program is a structured, 4-week training pathway for neurodivergent creators. Learn professional digital skills using tools designed for how your brain actually works. Open to <b className="text-zinc-200">Self-Managed</b> and <b className="text-zinc-200">Plan-Managed</b> NDIS participants.
            </p>
            <div className="flex flex-col gap-3 mt-10 max-w-md">
              <Link data-testid="ndis-cta-intake" to="/ndis-portal" className="bg-nc-orange hover:bg-nc-orange/80 text-black font-sans font-semibold uppercase tracking-[0.2em] text-xs px-6 py-4 flex items-center justify-center gap-2 transition-colors">
                Start Your Registration <ArrowRight className="w-4 h-4" />
              </Link>
              <a data-testid="ndis-cta-packet" href={PACKET_URL} className="border border-white/20 hover:border-white/50 text-zinc-200 font-sans font-semibold uppercase tracking-[0.2em] text-xs px-6 py-4 flex items-center justify-center gap-2 transition-colors">
                <Download className="w-4 h-4" /> Download Coordinator Info Packet (PDF)
              </a>
              <p className="font-mono text-[10px] text-zinc-600 mt-1">No gaming experience needed. No coding. Work at your own pace, with a clear plan.</p>
            </div>
          </div>
        </section>

        {/* 2 — BOTTLENECK VS SOLUTION */}
        <section className="border-b border-white/10">
          <div className="max-w-4xl mx-auto px-6 py-16 sm:py-20">
            <h2 data-testid="ndis-bottleneck-title" className="font-sans font-bold text-2xl sm:text-3xl tracking-tight flex items-center gap-3">
              <BrainCircuit className="w-6 h-6 text-nc-blue" /> Built for Brains That Think Differently
            </h2>
            <p className="text-base text-zinc-400 leading-relaxed mt-5 max-w-3xl">
              Traditional 3D modelling asks you to master complicated software, memorise hundreds of menus, and write code — all before you can make anything. For many autistic and ADHD creators, that wall of boring technical steps is what stops a real talent from ever getting started. That's not a motivation problem. It's a design problem.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-10">
              <div data-testid="ndis-new-way" className="border border-nc-blue/40 bg-nc-blue/5 p-6 order-first md:order-last">
                <p className="font-mono text-[10px] tracking-[0.25em] text-nc-blue uppercase mb-4">The Specpixel Way</p>
                <ul className="flex flex-col gap-3">
                  {NEW_WAY.map((t) => (
                    <li key={t} className="flex items-start gap-3 text-sm text-zinc-200 leading-relaxed">
                      <CheckCircle2 className="w-4 h-4 text-nc-blue shrink-0 mt-0.5" /> {t}
                    </li>
                  ))}
                </ul>
              </div>
              <div data-testid="ndis-old-way" className="border border-white/10 bg-black/40 p-6">
                <p className="font-mono text-[10px] tracking-[0.25em] text-zinc-500 uppercase mb-4">Traditional 3D Software</p>
                <ul className="flex flex-col gap-3">
                  {OLD_WAY.map((t) => (
                    <li key={t} className="flex items-start gap-3 text-sm text-zinc-500 leading-relaxed">
                      <span className="w-4 h-4 shrink-0 mt-0.5 border border-white/20 flex items-center justify-center font-mono text-[9px] text-zinc-600">×</span> {t}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            <p className="text-base text-zinc-400 leading-relaxed mt-8 max-w-3xl">
              Our custom Image-to-3D engine works like a cognitive tool: it handles the technical steps that cause overload, so you can put your energy into what neurodivergent creators are often naturally brilliant at — visual style, pattern recognition, creative problem-solving, and sustained deep focus on work that interests you.
            </p>
          </div>
        </section>

        {/* 3 — CURRICULUM */}
        <section className="border-b border-white/10">
          <div className="max-w-4xl mx-auto px-6 py-16 sm:py-20">
            <h2 data-testid="ndis-curriculum-title" className="font-sans font-bold text-2xl sm:text-3xl tracking-tight flex items-center gap-3">
              <CalendarCheck className="w-6 h-6 text-nc-orange" /> The 4-Week Intake Cohort: A Clear Plan, Week by Week
            </h2>
            <p className="text-base text-zinc-400 leading-relaxed mt-5 max-w-3xl">
              Every week has one clear goal, a short checklist, and a finished result you can point to. You always know what you're working on — and what "done" looks like.
            </p>
            <div className="flex flex-col gap-4 mt-10">
              {WEEKS.map((w) => (
                <div key={w.n} data-testid={`ndis-week-${w.n}`} className="border border-white/10 bg-black/40 p-6 grid grid-cols-[auto_1fr] gap-5">
                  <div className="w-12 h-12 border border-nc-orange/50 bg-nc-orange/10 flex items-center justify-center font-sans font-bold text-lg text-nc-orange shrink-0">{w.n}</div>
                  <div>
                    <h3 className="font-sans font-semibold text-lg text-zinc-100">{w.title}</h3>
                    <ul className="flex flex-col gap-2 mt-3">
                      {w.points.map((p) => (
                        <li key={p} className="flex items-start gap-3 text-sm text-zinc-400 leading-relaxed">
                          <span className="w-1.5 h-1.5 bg-nc-blue shrink-0 mt-1.5" /> {p}
                        </li>
                      ))}
                    </ul>
                    <p className="font-mono text-[11px] text-nc-green mt-4 border-t border-white/10 pt-3">You finish this week with: {w.outcome}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* 4 — NDIS FUNDING & LINE ITEMS */}
        <section className="border-b border-white/10">
          <div className="max-w-4xl mx-auto px-6 py-16 sm:py-20">
            <h2 data-testid="ndis-funding-title" className="font-sans font-bold text-2xl sm:text-3xl tracking-tight flex items-center gap-3">
              <FileText className="w-6 h-6 text-nc-blue" /> NDIS Funding & Line Items
            </h2>
            <p className="text-base text-zinc-400 leading-relaxed mt-5 max-w-3xl">
              This program is designed to sit within <b className="text-zinc-200">Capacity Building</b> supports. Below is everything a plan manager or support coordinator needs to process an invoice.
            </p>
            <div data-testid="ndis-funding-table" className="border border-white/15 bg-black/40 mt-8">
              {[
                ["Budget Category", "Capacity Building — Finding and Keeping a Job / Increased Social & Community Participation"],
                ["Line Item 1", "10_016_0102_5_3"],
                ["Line Item 2", "09_011_0125_6_3"],
                ["Individual modules", "$73.58 per hour (within NDIA standard price caps)"],
                ["Studio sprints (group)", "Compliant group ratios, charged per participant per hour at the applicable NDIA group rate"],
                ["Plan types accepted", "Self-Managed and Plan-Managed participants"],
              ].map(([k, v], i) => (
                <div key={k} className={`grid grid-cols-1 sm:grid-cols-[220px_1fr] gap-1 sm:gap-4 px-6 py-4 ${i > 0 ? "border-t border-white/10" : ""}`}>
                  <span className="font-mono text-[10px] tracking-[0.2em] text-zinc-500 uppercase pt-0.5">{k}</span>
                  <span className="font-mono text-sm text-zinc-100 font-bold break-words">{v}</span>
                </div>
              ))}
            </div>
            <div className="mt-8">
              <p className="font-mono text-[10px] tracking-[0.25em] text-nc-orange uppercase mb-4">How This Program Meets "Reasonable and Necessary"</p>
              <ul className="flex flex-col gap-4">
                {[
                  ["Builds technical independence", "participants acquire transferable digital skills (3D asset creation, quality control, digital storefront management) that reduce reliance on support over time."],
                  ["Fosters economic participation", "the program is explicitly goal-oriented toward employment pathways and independent micro-enterprise, aligning with Finding and Keeping a Job funding."],
                  ["Provides clear, measurable goals", "every week produces a documented, reviewable outcome suitable for plan reviews and progress reports."],
                ].map(([b, rest]) => (
                  <li key={b} className="flex items-start gap-3 text-sm text-zinc-400 leading-relaxed">
                    <CheckCircle2 className="w-4 h-4 text-nc-green shrink-0 mt-0.5" />
                    <span><b className="text-zinc-100">{b}</b> — {rest}</span>
                  </li>
                ))}
              </ul>
            </div>
            <p className="font-mono text-[10px] text-zinc-600 leading-relaxed mt-8 border-t border-white/10 pt-5">
              Line items and hourly rates are quoted against the current NDIA Pricing Arrangements and Price Limits. We verify all codes against the latest NDIA update before invoicing, and we're happy to provide a written service quote to your plan manager on request.
            </p>
          </div>
        </section>

        {/* 5 — OUTCOMES */}
        <section className="border-b border-white/10">
          <div className="max-w-4xl mx-auto px-6 py-16 sm:py-20">
            <h2 data-testid="ndis-outcomes-title" className="font-sans font-bold text-2xl sm:text-3xl tracking-tight">What You Walk Away With</h2>
            <p className="text-base sm:text-lg text-zinc-300 leading-relaxed mt-5 max-w-3xl">
              The goal of this program is independence. When the 4-week cohort ends, participants don't leave with a certificate in a drawer. They leave with a <b className="text-zinc-100">live, operational portfolio of professional 3D asset packs</b>, published on digital marketplaces under their own name — a real, working foundation for ongoing online self-employment.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-10">
              {OUTCOMES.map((o) => (
                <div key={o.title} data-testid={`ndis-outcome-${o.title.toLowerCase().replace(/[^a-z]+/g, "-")}`} className="border border-white/10 bg-black/40 p-6">
                  <o.icon className="w-5 h-5 text-nc-orange mb-4" />
                  <p className="font-sans font-semibold text-base text-zinc-100">{o.title}</p>
                  <p className="text-sm text-zinc-400 leading-relaxed mt-2">{o.body}</p>
                </div>
              ))}
            </div>
            <p className="font-mono text-[10px] text-zinc-600 leading-relaxed mt-8 border-t border-white/10 pt-5">
              Specpixel provides training, tools, and publishing support. We do not guarantee sales or income. Individual results depend on each participant's goals, effort, and market conditions.
            </p>
          </div>
        </section>

        {/* 6 — INTAKE FORM */}
        <section id="intake" className="border-b border-white/10 scroll-mt-24">
          <div className="max-w-2xl mx-auto px-6 py-16 sm:py-20">
            <h2 data-testid="ndis-intake-title" className="font-sans font-bold text-2xl sm:text-3xl tracking-tight">Start Your Intake Enquiry</h2>
            <p className="text-base text-zinc-400 leading-relaxed mt-4 mb-8">
              This takes about two minutes. A real person from our team will reply within two business days. There's no commitment and no cost to enquire.
            </p>
            <IntakeForm />
          </div>
        </section>

        {/* CLOSING — COORDINATORS */}
        <section>
          <div className="max-w-4xl mx-auto px-6 py-16 sm:py-20 text-center">
            <h2 data-testid="ndis-coordinator-title" className="font-sans font-bold text-xl sm:text-2xl tracking-tight">For Support Coordinators & Plan Managers</h2>
            <p className="text-base text-zinc-400 leading-relaxed mt-4 max-w-2xl mx-auto">
              Need documentation first? Download the Coordinator Info Packet — it includes the full curriculum outline, line-item mapping, risk and safeguarding summary, and a sample service agreement. Or submit an enquiry and we'll send everything directly.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 mt-8 justify-center">
              <a data-testid="ndis-cta-packet-bottom" href={PACKET_URL} className="border border-white/20 hover:border-white/50 text-zinc-200 font-sans font-semibold uppercase tracking-[0.2em] text-xs px-6 py-4 flex items-center justify-center gap-2 transition-colors">
                <Download className="w-4 h-4" /> Download Coordinator Info Packet
              </a>
              <Link data-testid="ndis-cta-intake-bottom" to="/ndis-portal" className="bg-nc-orange hover:bg-nc-orange/80 text-black font-sans font-semibold uppercase tracking-[0.2em] text-xs px-6 py-4 flex items-center justify-center gap-2 transition-colors">
                Start Your Registration <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}
